import React, { useEffect, useRef, useState } from "react";
import { motion, useScroll, useSpring, useTransform } from "framer-motion";
import { ArrowDown } from "lucide-react";
import HeroScene from "@/components/HeroScene";

const fadeUp = (delay = 0) => ({
    initial: { opacity: 0, y: 30 },
    animate: { opacity: 1, y: 0 },
    transition: { duration: 1.1, delay, ease: [0.22, 1, 0.36, 1] },
});

// Word-by-word reveal for the headline
const RevealLine = ({ children, delay = 0, className = "" }) => (
    <span className={`inline-block overflow-hidden ${className}`}>
        <motion.span
            initial={{ y: "115%" }}
            animate={{ y: "0%" }}
            transition={{ duration: 1.2, delay, ease: [0.22, 1, 0.36, 1] }}
            className="inline-block"
        >
            {children}
        </motion.span>
    </span>
);

// Magnetic CTA button — gently pulls towards cursor on hover
const MagneticCTA = ({ href, children, primary = false, testid }) => {
    const ref = useRef(null);
    const [pos, setPos] = useState({ x: 0, y: 0 });

    const onMove = (e) => {
        const rect = ref.current?.getBoundingClientRect();
        if (!rect) return;
        const x = e.clientX - rect.left - rect.width / 2;
        const y = e.clientY - rect.top - rect.height / 2;
        setPos({ x: x * 0.18, y: y * 0.18 });
    };
    const onLeave = () => setPos({ x: 0, y: 0 });

    return (
        <motion.a
            ref={ref}
            href={href}
            data-testid={testid}
            onMouseMove={onMove}
            onMouseLeave={onLeave}
            animate={{ x: pos.x, y: pos.y }}
            transition={{ type: "spring", stiffness: 200, damping: 18, mass: 0.4 }}
            className={
                primary
                    ? "group inline-flex items-center gap-3 px-6 md:px-7 py-3 md:py-3.5 bg-bisleri text-white font-mono text-[10px] md:text-[11px] tracking-[0.25em] uppercase hover:bg-bisleri-deep transition-colors rounded-sm shadow-lg shadow-bisleri/25"
                    : "group inline-flex items-center gap-3 px-6 md:px-7 py-3 md:py-3.5 border border-bisleri/40 text-bisleri font-mono text-[10px] md:text-[11px] tracking-[0.25em] uppercase hover:bg-bisleri-light transition-colors rounded-sm"
            }
        >
            {children}
        </motion.a>
    );
};

const Hero = () => {
    const [isMobile, setIsMobile] = useState(false);
    useEffect(() => {
        const check = () => setIsMobile(window.innerWidth < 768);
        check();
        window.addEventListener("resize", check);
        return () => window.removeEventListener("resize", check);
    }, []);

    // Scroll-linked parallax for foreground (light cinematic touch)
    const sectionRef = useRef(null);
    const { scrollYProgress } = useScroll({
        target: sectionRef,
        offset: ["start start", "end start"],
    });
    const yText = useTransform(scrollYProgress, [0, 1], [0, -90]);
    const yTextSm = useSpring(yText, { stiffness: 100, damping: 28 });

    return (
        <section
            ref={sectionRef}
            id="top"
            data-testid="hero-section"
            className="relative min-h-[100svh] pt-28 md:pt-32 pb-16 overflow-hidden bg-sage blueprint-grid"
        >
            {/* Warm wash */}
            <div
                className="absolute inset-0 z-0 pointer-events-none"
                style={{
                    background:
                        "radial-gradient(ellipse at 75% 25%, rgba(244,239,227,0.85) 0%, rgba(238,242,234,0.6) 55%, rgba(238,242,234,0) 100%)",
                }}
            />

            {/* 3D scene */}
            <div className="absolute inset-0 z-[1] pointer-events-none" style={{ opacity: isMobile ? 0.7 : 1 }}>
                <HeroScene mobile={isMobile} />
            </div>

            {/* Veil ensures legibility */}
            <div
                className="absolute inset-0 z-[2] pointer-events-none"
                style={{
                    background: isMobile
                        ? "linear-gradient(180deg, rgba(238,242,234,0.55) 0%, rgba(238,242,234,0.9) 50%, rgba(238,242,234,1) 100%)"
                        : "radial-gradient(ellipse at 20% 55%, rgba(238,242,234,0.96) 0%, rgba(238,242,234,0.55) 50%, rgba(238,242,234,0) 80%)",
                }}
            />

            {/* Editorial frame — corner ticks */}
            <div className="absolute inset-6 md:inset-10 z-[3] pointer-events-none">
                <div className="absolute top-0 left-0 w-6 h-6 border-t border-l border-ink-900/15" />
                <div className="absolute top-0 right-0 w-6 h-6 border-t border-r border-ink-900/15" />
                <div className="absolute bottom-0 left-0 w-6 h-6 border-b border-l border-ink-900/15" />
                <div className="absolute bottom-0 right-0 w-6 h-6 border-b border-r border-ink-900/15" />
            </div>

            <motion.div
                style={{ y: yTextSm }}
                className="relative z-10 max-w-[1400px] mx-auto px-6 md:px-12 grid grid-cols-12 gap-6 min-h-[78vh]"
            >
                <div className="col-span-12 lg:col-span-2 flex lg:flex-col justify-between items-start pt-2">
                    <motion.div {...fadeUp(0.1)} className="eyebrow">
                        Est. <span className="text-bisleri">2017</span>
                    </motion.div>
                    <motion.div
                        {...fadeUp(0.3)}
                        className="hidden lg:block eyebrow self-end"
                        style={{ writingMode: "vertical-rl", transform: "rotate(180deg)" }}
                    >
                        Mumbai · India
                    </motion.div>
                </div>

                <div className="col-span-12 lg:col-span-7 flex flex-col justify-center">
                    <motion.div {...fadeUp(0.2)} className="section-number mb-6 flex items-center gap-3">
                        <span className="w-8 h-px bg-bisleri" />
                        00 / Prologue
                    </motion.div>

                    <h1 className="font-display text-[42px] sm:text-6xl md:text-7xl lg:text-[88px] xl:text-[104px] leading-[0.95] tracking-tight text-ink-900">
                        <RevealLine delay={0.25}>Healing</RevealLine>
                        <br />
                        <RevealLine delay={0.4}>is&nbsp;a&nbsp;</RevealLine>
                        <RevealLine delay={0.55} className="text-bisleri italic">Right</RevealLine>
                        <RevealLine delay={0.6}>,</RevealLine>
                        <br />
                        <RevealLine delay={0.7} className="text-clinicgreen-deep italic">
                            not a privilege.
                        </RevealLine>
                    </h1>

                    <motion.div {...fadeUp(0.85)} className="mt-8 md:mt-10 max-w-xl">
                        <p className="font-body text-[15px] sm:text-base md:text-lg text-ink-700 leading-relaxed">
                            <span className="text-ink-900 font-semibold">Dr. Rahul Ghule</span>, MBBS
                            (KEM, Mumbai) — physician, social entrepreneur, and founder of
                            <span className="text-bisleri font-semibold"> One Rupee Clinic</span>. A pan-India
                            mission to deliver primary &amp; emergency care for the price of a single rupee.
                        </p>
                    </motion.div>

                    <motion.div {...fadeUp(1.0)} className="mt-8 md:mt-10 flex flex-wrap items-center gap-4 md:gap-5">
                        <MagneticCTA href="#mission" primary testid="hero-cta-primary">
                            The Mission
                            <span className="inline-block transition-transform group-hover:translate-x-1">→</span>
                        </MagneticCTA>
                        <MagneticCTA href="#about" testid="hero-cta-secondary">
                            Read Biography
                        </MagneticCTA>
                    </motion.div>
                </div>

                {/* Stats column */}
                <div className="col-span-12 lg:col-span-3 mt-10 lg:mt-0 grid grid-cols-3 lg:flex lg:flex-col lg:justify-end gap-4 md:gap-6 bg-sage/85 lg:bg-transparent backdrop-blur-sm lg:backdrop-blur-0 rounded-sm p-4 lg:p-0">
                    {[
                        ["Patients", "10L+", "text-ink-900"],
                        ["Railway EMRs", "33", "text-ink-900"],
                        ["Consultation Fee", "₹1", "text-clinicgreen-deep"],
                    ].map(([label, val, color], i) => (
                        <motion.div
                            key={label}
                            {...fadeUp(0.7 + i * 0.12)}
                            className="border-t border-ink-900/15 pt-3 lg:pt-5"
                        >
                            <div className="eyebrow mb-1 lg:mb-2 text-[9px] lg:text-[10px]">{label}</div>
                            <div className={`font-display text-2xl md:text-3xl lg:text-5xl ${color}`}>
                                {val}
                                {label === "Consultation Fee" && (
                                    <span className="text-ink-500 text-sm lg:text-xl ml-1 lg:ml-2 font-body">only</span>
                                )}
                            </div>
                        </motion.div>
                    ))}
                </div>
            </motion.div>

            {/* Scroll cue */}
            <motion.a
                href="#about"
                {...fadeUp(1.3)}
                className="absolute bottom-4 md:bottom-6 left-1/2 -translate-x-1/2 z-10 flex flex-col items-center gap-2 text-ink-500 hover:text-bisleri transition-colors"
                data-testid="hero-scroll-cue"
            >
                <span className="font-mono text-[10px] tracking-[0.3em] uppercase">Scroll</span>
                <ArrowDown size={14} className="animate-bounce" />
            </motion.a>
        </section>
    );
};

export default Hero;
